import type { Name, TaskItemResponse } from '@lmc/ts-common'
import { Avatar, ThreeDots, theme } from '@lmc/ui-kit'
import {
  ImageBouquet,
  ListItem,
  StatusBadge
} from '@lmc/ui-kit/src/dataDisplay'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Popover } from 'antd'
import { useAbilityAccess } from 'components/AccessControl'
import PopoverOption from 'components/generic/PopoverOption'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import StatusDropdown from './StatusDropdown'
import TaskModal from './taskModals/TaskModals'

type TaskProps = {
  task: TaskItemResponse
  refetchDailyTasks: () => Promise<any>
  refetchTasks: () => Promise<any>
}

const AVATAR_SIZE = 40

const Task = ({ task, refetchDailyTasks, refetchTasks }: TaskProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const canUpdateTask = useAbilityAccess([{ i: 'update', this: 'Task' }])
  const [openMenuPopover, setOpenMenuPopover] = useState(false)
  const [openMembersPopover, setOpenMembersPopover] = useState(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [modalType, setModalType] = useState('')
  const { calculatedStatus, residentData, assignee, title, assigneeData } = task

  const handleOpenModal = (modalType: string) => {
    setModalType(modalType)
    setShowModal(true)
    setOpenMenuPopover(false)
  }

  const handleOpenMenuChange = (newOpen: boolean) => {
    setOpenMenuPopover(newOpen)
  }

  const getName = (name: Name | string) => {
    if (typeof name === 'string') {
      return name
    }

    return `${name.first} ${name.last}`
  }

  return (
    <>
      <Wrapper
        icon={
          residentData && (
            <TaskIcon>
              <Avatar size={AVATAR_SIZE} url={residentData.picture} />
            </TaskIcon>
          )
        }
        title={
          <TaskTitle level={5} weight="medium">
            {residentData
              ? // some ts common issue here
                getName(residentData.name)
              : title}
          </TaskTitle>
        }
        rightChildren={
          <TaskRightChildren>
            <DesktopStatusContainer>
              <StatusDropdown
                status={calculatedStatus}
                taskId={task.id}
                task={task}
                canUpdate={canUpdateTask}
                onSuccess={() => {
                  refetchDailyTasks()
                  refetchTasks()
                }}
              />
            </DesktopStatusContainer>
            <MobileStatusBadge status={calculatedStatus} />
            {assignee?.length && (
              <ImageBouquet
                images={assigneeData.map((asignee) => asignee.picture)}
                maximumRenderedItems={3}
                popoverContent={
                  <>
                    <MembersPopoverText level={2}>
                      {t(toDosConstants.TEAM_MEMBERS)}
                    </MembersPopoverText>
                    {assigneeData.map((asignee) => (
                      <MembersPopoverOption key={asignee.id}>
                        <Avatar size={18} url={asignee.picture} />
                        <Text>{asignee.name}</Text>
                      </MembersPopoverOption>
                    ))}
                  </>
                }
                open={openMembersPopover}
                openChangeFunction={setOpenMembersPopover}
              />
            )}
            {canUpdateTask && (
              <Popover
                placement="leftTop"
                onOpenChange={handleOpenMenuChange}
                open={openMenuPopover}
                showArrow={false}
                content={
                  <PopoverContent>
                    <PopoverOption onClick={() => handleOpenModal('status')}>
                      <Text>{t(toDosConstants.CHANGE_STATUS)}</Text>
                    </PopoverOption>
                    <PopoverOption onClick={() => handleOpenModal('snooze')}>
                      <Text>{t(toDosConstants.SNOOZE)}</Text>
                    </PopoverOption>
                    <PopoverOption onClick={() => handleOpenModal('delete')}>
                      <Text>{t(toDosConstants.DELETE)}</Text>
                    </PopoverOption>
                  </PopoverContent>
                }
                trigger="click"
              >
                <MenuContainer>
                  <ThreeDots
                    color={
                      openMenuPopover
                        ? theme.color.status.primary
                        : theme.color.neutral.color3
                    }
                  />
                </MenuContainer>
              </Popover>
            )}
          </TaskRightChildren>
        }
      />
      <TaskModal
        task={task}
        isModalVisible={showModal}
        setIsModalVisible={setShowModal}
        modalType={modalType}
        refetchDailyTasks={refetchDailyTasks}
        refetchTasks={refetchTasks}
      />
    </>
  )
}

const Wrapper = styled(ListItem)`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: 16px;
  padding: 12px 16px;
  margin-top: 12px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 4px 8px;
  }
`

const TaskRightChildren = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    gap: 8px;
  }
`

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const TaskIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50px;
  overflow: hidden;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const TaskTitle = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const PopoverContent = styled.div``

const MembersPopoverText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const MobileStatusBadge = styled(StatusBadge)`
  display: none;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: flex;
  }
`
const DesktopStatusContainer = styled.div`
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const MembersPopoverOption = styled.div`
  display: flex;
  cursor: pointer;
  gap: 4px;
  padding: 4px;
  border-radius: 9px;
`

export default Task
